import React from "react";

import { AvailabilitySection, LoadSection, PerformanceSection, WellnessSection } from "../ui/athleteSections";
import {
  AdviceSection,
  LegalFooter,
  LockedSection,
  MealHabitSection,
  ReportHeader,
  SessionHistorySection,
  StateCardSection,
  athleteMeta,
} from "../ui/reportSections";

const SCOUT_NOTICE =
  "Vista externa: solo métricas de rendimiento y disponibilidad. Sueño, ánimo, motivación, dolor y alimentación son datos sensibles y no se comparten sin autorización expresa del deportista y sus representantes.";

const stateChip = (a) => (a.state && a.score !== null ? [{ label: `${a.state.name} · ${a.score}%`, color: a.state.color }] : []);

/** "Mi reporte" — the athlete's own report. */
export const AthleteSelfReport = ({ data }) => {
  const a = data.athlete;
  const checkins = a.checkin_adherence === null ? 0 : Math.round((a.checkin_adherence / 100) * data.period.days);
  return (
    <>
      <ReportHeader
        eyebrow="Athletain · Mi reporte"
        title={a.full_name}
        subtitle={[a.club, a.category, a.position, a.sport].filter(Boolean).join(" · ")}
        chips={[
          ...stateChip(a),
          { label: `Últimos ${data.period.days} días` },
          { label: `${a.sessions_recorded} días con sesión · ${checkins} check-ins` },
        ]}
        meta={athleteMeta(data)}
      />
      <StateCardSection report={data} title="Mi estado" stateCopy={data.state_copy} />
      <PerformanceSection report={data} />
      <SessionHistorySection report={data} withState />
      <LoadSection report={data} />
      <WellnessSection report={data} />
      <MealHabitSection report={data} title="Mi alimentación" />
      <AdviceSection title="Esta semana" meta="Según tus datos del periodo" items={data.advice} />
    </>
  );
};

/** Coach or scout drill-down. `view` and `data_sharing` come from the server's
 *  access rules; the page only picks the matching layout. */
export const AthleteDetailReport = ({ data }) => {
  const a = data.athlete;
  const subtitle = [a.club, a.category, a.position].filter(Boolean).join(" · ");

  if (data.view === "scout") {
    return (
      <>
        <ReportHeader
          eyebrow="Reporte del deportista · Vista externa"
          title={a.full_name}
          subtitle={subtitle}
          chips={[
            ...(a.performance_index !== null ? [{ label: `Índice dep. ${a.performance_index}/100` }] : []),
            ...(a.age !== null ? [{ label: `${a.age} años` }] : []),
            { label: `${a.sessions_recorded} días con sesión` },
          ]}
          meta={athleteMeta(data)}
          notice={SCOUT_NOTICE}
        />
        <AvailabilitySection report={data} />
        <PerformanceSection report={data} />
        <LockedSection
          title="Datos bloqueados"
          items={["Bienestar", "Alimentación y nutrición", "Carga interna y ratio A:C"]}
          text="Requieren autorización expresa del deportista. Para solicitar acceso a la ficha completa, contacta al club. Ley 1581 de 2012: el tratamiento de datos de menores requiere autorización de padres o representantes."
        />
      </>
    );
  }

  const shared = data.data_sharing === true;
  return (
    <>
      <ReportHeader
        eyebrow="Reporte del deportista · Vista de entrenador"
        title={a.full_name}
        subtitle={subtitle}
        chips={[
          ...stateChip(a),
          ...(a.performance_index !== null ? [{ label: `Índice dep. ${a.performance_index}/100` }] : []),
          { label: `${a.sessions_recorded} días con sesión` },
          shared ? { label: "Datos compartidos", color: "#0f8a6a" } : { label: "Sin autorización de bienestar", color: "#7a4b00" },
        ]}
        meta={athleteMeta(data)}
      />
      {shared ? <StateCardSection report={data} title="Estado" /> : <AvailabilitySection report={data} />}
      <PerformanceSection report={data} />
      <SessionHistorySection report={data} withState={shared} />
      {shared ? (
        <>
          <LoadSection report={data} />
          <WellnessSection report={data} />
          <MealHabitSection report={data} title="Alimentación" />
          <AdviceSection title="Qué hacer" meta="Según los datos del periodo" items={data.advice} />
        </>
      ) : (
        <LockedSection
          title="Bienestar, carga y alimentación"
          items={["Bienestar", "Carga interna", "Alimentación"]}
          text="El deportista no autorizó compartir su check-in con la academia. Puede habilitarlo desde Configuración en su app."
        />
      )}
    </>
  );
};

export { LegalFooter };
